import React, { useState } from 'react';
import { MainProduct } from '../../types/studio';
import { useStudioStore } from '../../stores/useStudioStore';
import { studioApiService } from '../../services/studioApiService';

interface Props {
  product: MainProduct;
}

export const ResultRow: React.FC<Props> = ({ product }) => {
  const { updateMainProduct, addUpscalerItem, addDbUpload } = useStudioStore();
  const [isUploadingS3, setIsUploadingS3] = useState(false);

  // Approve -> moves to Upscaler tab directly
  const handleApprove = () => {
    updateMainProduct(product.id, { reviewState: 'approved' });
    if (product.result?.fullSrc) {
      addUpscalerItem({
        id: crypto.randomUUID(),
        productId: product.productId || 'Unassigned',
        fullSrc: product.result.fullSrc,
        selected: true,
        status: 'pending'
      });
    }
  };

  const handleDiscard = () => {
    updateMainProduct(product.id, { reviewState: 'discarded' });
  };

  const handleUploadS3 = async () => {
    if (!product.productId) {
      alert("Please assign a Product ID to this item before uploading!");
      return;
    }
    if (!product.result?.quadrants || product.result.quadrants.length < 4) {
      alert("Quadrants are not ready yet.");
      return;
    }
    
    setIsUploadingS3(true);
    try {
      const res = await studioApiService.uploadToS3(product.productId, product.result.quadrants);
      if (res.success) {
        // Send to DB Upload Queue
        addDbUpload({
          rowId: product.productId,
          imageUrls: res.uploaded,
          status: 'pending'
        });
        alert(`Uploaded to S3! Go to the DB Upload tab to finish.`);
      }
    } catch (e: any) {
      alert(`S3 Upload failed: ${e.message}`);
    } finally {
      setIsUploadingS3(false);
    }
  };

  return (
    <div className={`result-row status-${product.status}`}>
      <div className="result-row-header">
        <div className="result-row-id">
          Product: {product.productId || 'Unassigned'} 
          <span className={`status-badge ${product.status}`}>{product.status}</span>
        </div>
      </div>
      
      <div className="result-row-body">
        <div className="img-cell">
          <div className="img-cell-label">Input</div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {product.images.map((img, i) => (
              <img key={i} src={img.previewUrl} className="row-img row-img-input" alt="input" />
            ))}
          </div>
        </div>
        
        <div className="row-arrow">→</div>
        
        <div className="img-cell">
          <div className="img-cell-label">Result</div>
          {product.status === 'awaiting-approval' && product.result?.fullSrc ? (
            <img 
              src={product.result.fullSrc.startsWith('data:') || product.result.fullSrc.startsWith('http') 
                ? product.result.fullSrc 
                : `data:image/png;base64,${product.result.fullSrc}`
              } 
              className="row-img row-img-full" 
              alt="result" 
            />
          ) : product.status === 'complete' && product.result?.quadrants ? (
            <div className="quad-grid">
              {product.result.quadrants.map((q, i) => (
                <img key={i} src={q.startsWith('http') ? q : `data:image/png;base64,${q}`} className="row-img row-img-quad" alt="quad" />
              ))}
            </div>
          ) : (
            <div className="skel-img row-img-full">
              <div className="skel-inner">
                <div className="dots"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}
        </div>

        <div className="row-actions">
          {product.status === 'awaiting-approval' && (
            <>
              <button className="row-act-btn row-act-approve" onClick={handleApprove}>✓ Approve</button>
              <button className="row-act-btn row-act-discard" onClick={handleDiscard}>✕ Discard</button>
            </>
          )}
          
          {product.status === 'complete' && product.reviewState === 'approved' && (
            <button 
              className="row-act-btn" 
              style={{ background: 'var(--blue)', color: '#fff' }}
              onClick={handleUploadS3}
              disabled={isUploadingS3}
            >
              {isUploadingS3 ? 'Uploading...' : '↑ Upload to S3'}
            </button>
          )}

          {product.error && <div className="row-error">{product.error}</div>}
        </div>
      </div>
    </div>
  );
};
